import { useDispatch, useSelector } from 'react-redux'
import {
  IQuest,
  setCurrentSeasonProgress,
  setRecentlyCompletedQuests,
  UserSeasonProgress,
} from '../../../modules/seasons/slice'
import {
  selectCurrentSeason,
  selectCurrentSeasonProgress,
} from '../../../modules/seasons/selectors'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Box } from '../../../shared_components/UI/Box'
import { improvedAmountFromWeiFloat } from '../../../contexts/StakingContext'
import { showDecimalsIfNeeded } from '../../Dashboard/Allocations/InvestmentInfo'
import { selectEthUsdPrice } from '../../../modules/web3/selectors'

import { format } from 'date-fns'
import { Span } from '../../../shared_components/Library/Typeography'
import { QuestWithProgress, QuestWithProgressCard } from './Progress'
import { Seperator } from '../../Memecoins/TokenPage/Threads/ThreadList'
import { useHistory, useLocation } from 'react-router-dom'
import routes from '../../../router/routes'
import { setDismissableModal } from '../../../modules/miscUi/actions'

// in here we take user season progress from websocket and save to state,
// then decide if we want to display a toast, or eat a bag of jerky

// string is value in wei, bool = isPurchase
type DataPacket = [UserSeasonProgress, string, string, boolean]

export function useCarbsOrKeto() {
  const dispatch = useDispatch()
  const oldUserSeasonProgress = useSelector(selectCurrentSeasonProgress)
  const {
    meta_data: { quests },
  } = useSelector(selectCurrentSeason) ?? { meta_data: { quests: [] } }

  const history = useHistory()

  function navToProgressOverview() {
    history.push(routes.season.index)
    toast.dismiss()
  }

  function carbsOrKeto(data: DataPacket, symbol: string) {
    const [userSeasonProgress, ethValue, tokensValue, isPurchase] = data

    const questsToShow: (QuestWithProgress & { isComplete?: boolean })[] = []

    let hasAnyCompletions = false

    for (let i = 0; i < userSeasonProgress.quests.length; i++) {
      // detect difference beween old and new user season progress
      const currentQuestProgress = userSeasonProgress.quests[i]
      const { questId } = currentQuestProgress
      const currentQuest = quests.find(
        (quest: IQuest) => quest.questId === questId,
      )
      if (!currentQuest) {
        toast.error(`Quest with id ${questId} not found in current season`)
        continue
      }

      const questProgressRequirement = BigInt(currentQuest.progressRequirement)

      const oldProgress = !!oldUserSeasonProgress
        ? BigInt(oldUserSeasonProgress.quests[i].progress)
        : 0n
      const currentProgress = BigInt(userSeasonProgress.quests[i].progress)
      const difference = currentProgress - oldProgress

      // we never want to show this in toast, just quest complete
      const isTradeVolumeSeasonal =
        currentQuest.objective === 'trade_volume' &&
        currentQuest.duration !== 'daily'

      const questComplete = currentProgress >= questProgressRequirement
      if (difference > 0 && !isTradeVolumeSeasonal) {
        questsToShow.push({
          ...currentQuest,
          ...currentQuestProgress,
          oldProgress: oldProgress.toString(),
          isComplete: questComplete,
        })
        if (!hasAnyCompletions) hasAnyCompletions = questComplete
      }
      // also detect completion
    }

    // if we have completions allow the toast and show a fullscreen modal of the quest complete screen
    dispatch(setCurrentSeasonProgress(userSeasonProgress))
    if (hasAnyCompletions) {
      dispatch(setDismissableModal('quest_complete'))
      dispatch(
        setRecentlyCompletedQuests(
          questsToShow
            .filter((q) => q.isComplete)
            .map((q) => ({
              questId: q.questId,
              oldProgress: q.oldProgress,
            })),
        ),
      )
      return
    }
    const questsProgressed = questsToShow.length
    toast(
      <Box dc g={16}>
        <TradeToast
          ethValue={ethValue}
          tokensValue={tokensValue}
          isPurchase={isPurchase}
          symbol={symbol}
        />
        <QuestToastWrapper dc g={16}>
          {questsToShow.slice(0, 1).map((quest, i) => (
            <>
              {(i < questsToShow.length - 1 || i === 0) && <Seperator />}
              <QuestWithProgressCard key={i} quest={quest} isCompact />
            </>
          ))}
          {questsProgressed > 1 && <Seperator />}
          {questsProgressed > 1 && (
            <Box
              ac
              jsb
              onClick={navToProgressOverview}
              style={{ cursor: 'pointer' }}
              as="a"
            >
              <Box>
                <Span c="text-secondary">
                  {questsProgressed - 1} more quest
                  {questsProgressed === 2 ? '' : 's'} progressed
                </Span>
              </Box>
              <Box fu aw>
                <img src="/img/icons/chevron-right.svg" alt="chevron right" />
              </Box>
            </Box>
          )}
        </QuestToastWrapper>
      </Box>,
      {
        hideProgressBar: true,
        style: {
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #333',
          background: '#080808',
        },
        closeButton: false,
      },
    )
  }

  return carbsOrKeto
}

const TradeToast = ({
  isPurchase,
  ethValue,
  tokensValue,
  symbol,
}: {
  isPurchase: boolean
  ethValue: string
  tokensValue: string
  symbol: string
}) => {
  const amount = Number(
    improvedAmountFromWeiFloat(tokensValue, 6),
  ).toLocaleString()

  const costInEth = showDecimalsIfNeeded(
    improvedAmountFromWeiFloat(ethValue),
    6,
  )

  const usdPerEth = useSelector(selectEthUsdPrice)
  const costInUsd = showDecimalsIfNeeded(Number(costInEth) * usdPerEth, 2)
  const date = format(new Date(), 'MMM dd, yyyy, HH:mm')

  console.log({ date, usdPerEth, costInUsd, costInEth, amount, tokensValue })

  return (
    <BuyToastWrapper jsb ac>
      <Box ac g={8}>
        <img src={`/img/icons/${isPurchase ? 'buy' : 'sell'}-circle.svg`} />
        <Box dc>
          <Span c="text-primary" s="L" fw="semibold">
            {isPurchase ? 'Buy' : 'Sell'}
          </Span>
          <Span c="text-secondary" s="S">
            {date}
          </Span>
        </Box>
      </Box>
      <Box dc fu aw g={2} afe>
        <Span c="text-primary" s="L" fw="semibold">
          {amount}{' '}
          <Span s="XS" c="text-secondary" fw="regular">
            {symbol}
          </Span>
        </Span>
        <Span c="text-secondary" s="S">
          ${costInUsd}
        </Span>
      </Box>
    </BuyToastWrapper>
  )
}

const BuyToastWrapper = styled(Box)`
  img {
    width: 32px;
    height: 32px;
  }
`
const QuestToastWrapper = styled(Box)`
  ${Seperator} {
    margin: 0;
    opacity: 1;
    background: var(--stroke-tertiary, #252525);
  }
`
